// ---------------------------------------------------------------------------
// CLIENT-ONBOARDING-UI-0001 R1G — Deterministic structural GeoJSON validator
//
// Pure functions.  No external dependencies.  Independently testable.
// Validates position, geometry, feature, and feature-collection structure
// per RFC 7946 semantics.  Does NOT perform topology or CRS validation.
//
// R1G adds:
//  - Exact LinearRing closure (full Position equality, not just lon/lat)
//  - Standard GeoJSON bbox structural validation
// ---------------------------------------------------------------------------

// ---- Supported top-level types ----------------------------------------------

const GEOMETRY_TYPES = [
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
  'GeometryCollection',
] as const;

const TOP_LEVEL_TYPES = [
  'FeatureCollection',
  'Feature',
  ...GEOMETRY_TYPES,
] as const;

type GeoJsonType = (typeof TOP_LEVEL_TYPES)[number];

// ---- Position ---------------------------------------------------------------

/**
 * A valid GeoJSON Position is an array of at least 2 finite numbers.
 * Additional coordinate dimensions are allowed if finite.
 */
export function isValidPosition(pos: unknown): pos is number[] {
  if (!Array.isArray(pos)) return false;
  if (pos.length < 2) return false;
  for (let i = 0; i < pos.length; i++) {
    if (typeof pos[i] !== 'number' || !isFinite(pos[i])) return false;
  }
  return true;
}

// ---- Position equality ------------------------------------------------------

/**
 * Compare two valid Positions for exact equality.
 *
 * Both positions must have the same length (dimensionality) and every
 * coordinate value must be identical.  This is stricter than comparing
 * only longitude / latitude — it ensures complete positional equivalence
 * including elevation and any additional coordinate dimensions.
 */
export function positionsEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ---- LinearRing -------------------------------------------------------------

function isValidLinearRing(coords: unknown): coords is number[][] {
  if (!Array.isArray(coords)) return false;
  if (coords.length < 4) return false;

  for (const pos of coords) {
    if (!isValidPosition(pos)) return false;
  }

  // First and last position must be exactly equivalent (all dimensions)
  const first = coords[0] as number[];
  const last = coords[coords.length - 1] as number[];
  if (!positionsEqual(first, last)) return false;

  return true;
}

// ---- Bbox structural validation ---------------------------------------------

/**
 * Validate a GeoJSON bbox member structurally.
 *
 * A valid bbox MUST be:
 *  - an array
 *  - of even length >= 4 (2 * N dimensions, N >= 2)
 *  - containing only finite numbers
 *
 * This is the basic structural check.  Dimensionality matching against
 * the enclosing geometry is performed by validateBboxForObject().
 */
function isValidBboxStructure(bbox: unknown): bbox is number[] {
  if (!Array.isArray(bbox)) return false;
  if (bbox.length < 4) return false;
  if (bbox.length % 2 !== 0) return false;
  for (let i = 0; i < bbox.length; i++) {
    if (typeof bbox[i] !== 'number' || !isFinite(bbox[i])) return false;
  }
  return true;
}

// ---- Dimension helpers ------------------------------------------------------

/**
 * Return the number of coordinate dimensions in a valid Position.
 */
function getPositionDimensions(pos: number[]): number {
  return pos.length;
}

// ---- R1H-01: Full position scanning helpers ----------------------------------

/**
 * Scan EVERY Position in an array and return the maximum coordinate
 * dimensionality.  Returns null if the array is empty or any element is not
 * a valid position array (length < 2).
 *
 * This replaces the previous behaviour of inspecting only the first position.
 */
function maxDimensionInPositions(positions: unknown[]): number | null {
  if (positions.length === 0) return null;
  let maxDim = 0;
  for (const pos of positions) {
    if (!Array.isArray(pos) || pos.length < 2) return null;
    if (pos.length > maxDim) maxDim = pos.length;
  }
  return maxDim;
}

/**
 * Scan every Position inside an array of rings (Polygon coordinate structure)
 * and return the maximum coordinate dimensionality.
 */
function maxDimensionInRings(rings: unknown[]): number | null {
  if (rings.length === 0) return null;
  let maxDim = 0;
  for (const ring of rings) {
    const dim = maxDimensionInPositions(ring as unknown[]);
    if (dim === null) return null;
    if (dim > maxDim) maxDim = dim;
  }
  return maxDim;
}

/**
 * Recursively determine the coordinate dimensionality of a valid Geometry.
 *
 * Returns the number of coordinate dimensions (e.g. 2 for [lon, lat],
 * 3 for [lon, lat, elev]) or null if dimensionality cannot be determined.
 *
 * R1H-01: For collection types, EVERY position is scanned — the maximum
 * represented dimensionality across all positions is returned, not just
 * the dimensionality of the first position.
 */
function getGeometryDimensions(geom: Record<string, unknown>): number | null {
  const type = geom.type as string;

  switch (type) {
    case 'Point': {
      const coords = geom.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return null;
      return getPositionDimensions(coords as number[]);
    }
    case 'MultiPoint':
    case 'LineString': {
      const coords = geom.coordinates;
      if (!Array.isArray(coords)) return null;
      return maxDimensionInPositions(coords as unknown[]);
    }
    case 'MultiLineString': {
      const lines = geom.coordinates;
      if (!Array.isArray(lines)) return null;
      let maxDim: number | null = null;
      for (const line of lines) {
        const dim = maxDimensionInPositions(line as unknown[]);
        if (dim === null) return null;
        if (maxDim === null || dim > maxDim) maxDim = dim;
      }
      return maxDim;
    }
    case 'Polygon': {
      const rings = geom.coordinates;
      if (!Array.isArray(rings)) return null;
      return maxDimensionInRings(rings as unknown[]);
    }
    case 'MultiPolygon': {
      const polys = geom.coordinates;
      if (!Array.isArray(polys)) return null;
      let maxDim: number | null = null;
      for (const poly of polys) {
        if (!Array.isArray(poly)) return null;
        const dim = maxDimensionInRings(poly as unknown[]);
        if (dim === null) return null;
        if (maxDim === null || dim > maxDim) maxDim = dim;
      }
      return maxDim;
    }
    case 'GeometryCollection': {
      const geoms = geom.geometries;
      if (!Array.isArray(geoms) || geoms.length === 0) return null;
      let maxDim: number | null = null;
      for (const g of geoms) {
        if (g && typeof g === 'object') {
          const dim = getGeometryDimensions(g as Record<string, unknown>);
          if (dim !== null && (maxDim === null || dim > maxDim)) {
            maxDim = dim;
          }
        }
      }
      return maxDim;
    }
    default:
      return null;
  }
}

/**
 * Recursively determine the coordinate dimensionality of any GeoJSON object.
 *
 * Supports Geometry, Feature, and FeatureCollection.  Returns the maximum
 * coordinate dimension represented, or null if dimensionality cannot be
 * determined (e.g. null geometry, empty collections).
 */
function getGeoJsonDimensions(obj: Record<string, unknown>): number | null {
  const type = obj.type as string;

  if (GEOMETRY_TYPES.includes(type as any)) {
    return getGeometryDimensions(obj);
  }

  if (type === 'Feature') {
    const geometry = obj.geometry;
    if (geometry === null) return null;
    if (!geometry || typeof geometry !== 'object') return null;
    return getGeometryDimensions(geometry as Record<string, unknown>);
  }

  if (type === 'FeatureCollection') {
    const features = obj.features;
    if (!Array.isArray(features) || features.length === 0) return null;
    let maxDim: number | null = null;
    for (const feat of features) {
      if (feat && typeof feat === 'object') {
        const dim = getGeoJsonDimensions(feat as Record<string, unknown>);
        if (dim !== null && (maxDim === null || dim > maxDim)) {
          maxDim = dim;
        }
      }
    }
    return maxDim;
  }

  return null;
}

/**
 * Validate the optional bbox member on a GeoJSON object.
 *
 * R1 fail-closed policy:
 *
 *  - When bbox is absent: valid (no further check).
 *  - When bbox is present: must pass structural validation AND the
 *    bbox length must equal 2 * coordinate dimensionality of the object.
 *  - If dimensionality cannot be determined (null geometry, empty
 *    FeatureCollection, empty GeometryCollection), reject the bbox
 *    rather than guessing dimensions.
 *
 * Mixed-dimension collections use the maximum represented dimensionality
 * to derive the expected bbox length.
 *
 * R1H-03: Axis ordering is enforced after dimension is known.
 * For dimension N, lower axes are bbox[0..N-1] and upper axes are
 * bbox[N..2N-1].  Axis 0 (longitude) allows reversal for antimeridian
 * representation.  All other axes require lower <= upper.
 */
function validateBboxForObject(obj: Record<string, unknown>): boolean {
  if (!('bbox' in obj)) return true;

  const bbox = obj.bbox;

  // Basic structural validation
  if (!isValidBboxStructure(bbox)) return false;

  // Determine expected dimensionality from the object
  const dim = getGeoJsonDimensions(obj);

  // R1 fail-closed: cannot determine dimensions → reject bbox
  if (dim === null) return false;

  // bbox length must equal 2 * coordinate dimensionality
  if (bbox.length !== 2 * dim) return false;

  // ---- R1H-03: Axis ordering validation ----
  // For dimension N:
  //   lower axes: bbox[0 .. N-1]
  //   upper axes: bbox[N .. 2N-1]
  //
  // Axis 0 (longitude): reversal allowed for antimeridian bbox
  // Axis 1 (latitude):  south <= north required
  // Axis >= 2:          lower <= upper required
  for (let axis = 0; axis < dim; axis++) {
    const lower = bbox[axis];
    const upper = bbox[axis + dim];

    // Longitude may reverse for antimeridian crossing
    if (axis === 0) continue;

    // Latitudes and all higher dimensions must be ordered
    if (lower > upper) return false;
  }

  return true;
}

// ---- RFC 7946 §7.1 member exclusivity (R1I) ---------------------------------

/**
 * Check whether a GeoJSON object carries members explicitly forbidden by
 * RFC 7946 §7.1 for its declared type.
 *
 * This implements ONLY the explicit RFC prohibitions.  Foreign / extension
 * members whose names happen to look like GeoJSON concepts are NOT rejected
 * unless §7.1 explicitly forbids that specific combination.
 *
 * RFC 7946 §7.1 prohibitions by type:
 *
 *   Geometry objects MUST NOT contain:  properties, features
 *   Feature MUST NOT contain:           geometries, features
 *   FeatureCollection MUST NOT contain: coordinates, geometry
 *
 * GeometryCollection additionally rejects a top-level "coordinates" member
 * as a STRUCTURAL requirement (GeometryCollection uses "geometries", not
 * "coordinates").  This is documented separately from §7.1.
 *
 * Returns true when the object contains a forbidden member.
 */
function hasDefiningMemberConflict(
  obj: Record<string, unknown>,
  geoJsonType: string,
): boolean {
  // ---- Geometry objects (§7.1) ----
  // MUST NOT contain: properties, features
  if (GEOMETRY_TYPES.includes(geoJsonType as any)) {
    if ('properties' in obj || 'features' in obj) return true;

    // GeometryCollection structural rule (not §7.1):
    // GeometryCollection is defined by "geometries", not "coordinates".
    // A top-level "coordinates" on a GeometryCollection contradicts its
    // structural definition per RFC 7946 §3.1.8.
    if (geoJsonType === 'GeometryCollection' && 'coordinates' in obj) return true;
  }

  // ---- Feature (§7.1) ----
  // MUST NOT contain: geometries, features
  if (geoJsonType === 'Feature') {
    if ('geometries' in obj || 'features' in obj) return true;
  }

  // ---- FeatureCollection (§7.1) ----
  // MUST NOT contain: coordinates, geometry
  if (geoJsonType === 'FeatureCollection') {
    if ('coordinates' in obj || 'geometry' in obj) return true;
  }

  return false;
}

// ---- Geometry validation ----------------------------------------------------

function isValidPointCoordinates(coords: unknown): boolean {
  return isValidPosition(coords);
}

function isValidMultiPointCoordinates(coords: unknown): boolean {
  if (!Array.isArray(coords)) return false;
  if (coords.length === 0) return false;
  return coords.every((pos) => isValidPosition(pos));
}

function isValidLineStringCoordinates(coords: unknown): boolean {
  if (!Array.isArray(coords)) return false;
  if (coords.length < 2) return false;
  return coords.every((pos) => isValidPosition(pos));
}

function isValidMultiLineStringCoordinates(coords: unknown): boolean {
  if (!Array.isArray(coords)) return false;
  return coords.every((ls) => isValidLineStringCoordinates(ls));
}

function isValidPolygonCoordinates(coords: unknown): boolean {
  if (!Array.isArray(coords)) return false;
  if (coords.length === 0) return false;
  return coords.every((ring) => isValidLinearRing(ring));
}

function isValidMultiPolygonCoordinates(coords: unknown): boolean {
  if (!Array.isArray(coords)) return false;
  return coords.every((poly) => isValidPolygonCoordinates(poly));
}

/**
 * Validate a Geometry object.  Requires `type` and `coordinates` (except
 * GeometryCollection which requires `geometries` instead).
 *
 * If the optional `bbox` member is present it must be structurally valid
 * and its length must match 2 * the coordinate dimensionality of the geometry.
 */
export function isValidGeometry(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') return false;
  const geom = obj as Record<string, unknown>;

  if (typeof geom.type !== 'string' || !GEOMETRY_TYPES.includes(geom.type as any)) {
    return false;
  }

  // R1H RFC sweep B: reject incompatible defining members
  if (hasDefiningMemberConflict(geom, geom.type)) return false;

  switch (geom.type) {
    case 'Point':
      if (!isValidPointCoordinates(geom.coordinates)) return false;
      break;
    case 'MultiPoint':
      if (!isValidMultiPointCoordinates(geom.coordinates)) return false;
      break;
    case 'LineString':
      if (!isValidLineStringCoordinates(geom.coordinates)) return false;
      break;
    case 'MultiLineString':
      if (!isValidMultiLineStringCoordinates(geom.coordinates)) return false;
      break;
    case 'Polygon':
      if (!isValidPolygonCoordinates(geom.coordinates)) return false;
      break;
    case 'MultiPolygon':
      if (!isValidMultiPolygonCoordinates(geom.coordinates)) return false;
      break;
    case 'GeometryCollection':
      if (!isValidGeometryCollectionGeometries(geom.geometries)) return false;
      break;
    default:
      return false;
  }

  // Validate optional bbox after geometry structure is confirmed valid
  return validateBboxForObject(geom);
}

function isValidGeometryCollectionGeometries(geometries: unknown): boolean {
  if (!Array.isArray(geometries)) return false;
  return geometries.every((g) => isValidGeometry(g));
}

// ---- Feature validation -----------------------------------------------------

/**
 * Validate a GeoJSON Feature.
 *
 * A Feature MUST have:
 *  - type: "Feature"
 *  - geometry: valid Geometry OR null
 *  - properties: object OR null (MUST be present per RFC 7946 semantics)
 *
 * R1H RFC sweep A: If `id` is present it MUST be a string or finite number.
 * Object, array, boolean, null, NaN, and Infinity are rejected.
 *
 * R1H RFC sweep B: Feature MUST NOT carry defining members of other GeoJSON
 * types (coordinates, geometries, features).
 *
 * If the optional `bbox` member is present it must be structurally valid
 * and its length must match 2 * the coordinate dimensionality of the feature.
 * A Feature with null geometry and a bbox is rejected (fail-closed).
 */
export function isValidFeature(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') return false;
  const feat = obj as Record<string, unknown>;

  if (feat.type !== 'Feature') return false;

  // R1H RFC sweep B: reject incompatible defining members
  if (hasDefiningMemberConflict(feat, 'Feature')) return false;

  // geometry MUST be present
  if (!('geometry' in feat)) return false;

  // geometry is valid Geometry OR null
  if (feat.geometry !== null && !isValidGeometry(feat.geometry)) return false;

  // properties MUST be present
  if (!('properties' in feat)) return false;

  // properties is object OR null
  if (feat.properties !== null && (typeof feat.properties !== 'object' || Array.isArray(feat.properties))) {
    return false;
  }

  // R1H RFC sweep A: Validate Feature.id when present
  if ('id' in feat) {
    const id = feat.id;
    const idType = typeof id;
    if (idType === 'string') {
      // string ids are valid
    } else if (idType === 'number') {
      if (!isFinite(id as number)) return false;
    } else {
      // object, array, boolean, null, undefined — all rejected
      return false;
    }
  }

  // Validate optional bbox member
  return validateBboxForObject(feat);
}

// ---- FeatureCollection validation -------------------------------------------

/**
 * Validate a GeoJSON FeatureCollection.
 *
 * Requires:
 *  - type: "FeatureCollection"
 *  - features: non-null array
 *  - every member is a structurally valid Feature
 *
 * An empty features array is accepted per RFC 7946 semantics.
 *
 * If the optional `bbox` member is present it must be structurally valid
 * and its length must match 2 * the coordinate dimensionality of the
 * collection.  An empty FeatureCollection with a bbox is rejected
 * (fail-closed — dimensionality cannot be determined).
 */
export function isValidFeatureCollection(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') return false;
  const fc = obj as Record<string, unknown>;

  if (fc.type !== 'FeatureCollection') return false;

  // R1H RFC sweep B: reject incompatible defining members
  if (hasDefiningMemberConflict(fc, 'FeatureCollection')) return false;

  if (!('features' in fc)) return false;
  if (!Array.isArray(fc.features)) return false;

  if (!fc.features.every((f) => isValidFeature(f))) return false;

  // Validate optional bbox member
  return validateBboxForObject(fc);
}

// ---- Top-level document validation ------------------------------------------

/**
 * Validate a complete GeoJSON document at the top level.
 *
 * Accepts: FeatureCollection, Feature, or any Geometry type as the root.
 * Rejects: missing type, unknown type, missing structural members, or
 * structurally invalid content.
 *
 * The optional `bbox` member is validated recursively — a malformed bbox
 * on any nested object invalidates the document.
 */
export function validateGeoJsonDocument(text: string): boolean {
  try {
    const obj = JSON.parse(text);
    if (!obj || typeof obj !== 'object') return false;
    if (typeof obj.type !== 'string') return false;

    const docType = obj.type as GeoJsonType;

    // FeatureCollection — validate as such (must have features array)
    if (docType === 'FeatureCollection') {
      return isValidFeatureCollection(obj);
    }

    // Feature — validate geometry + properties
    if (docType === 'Feature') {
      return isValidFeature(obj);
    }

    // Geometry — must have coordinates (or geometries for GeometryCollection)
    if (GEOMETRY_TYPES.includes(docType as any)) {
      return isValidGeometry(obj);
    }

    return false;
  } catch {
    return false;
  }
}

// ---- Re-export for convenience (backward-compatible with MappingStep) --------

/**
 * Lightweight check kept for fast-path rejection before full structural validation.
 * Still useful for the MappingStep UX — fast reject of obviously invalid JSON
 * before the more expensive structural walk.
 */
export function validateGeoJson(text: string): boolean {
  return validateGeoJsonDocument(text);
}
